import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, PartyPopper, Save, FileDown, Eye } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { exportAnniversaryCertificateToPDF } from "@/lib/contractPdfExport";
import { differenceInYears } from "date-fns";
import { saveDocumentToEmployeeFile, getDocumentCategory, getDocumentTypeName } from "@/lib/documentStorage";
import { PdfPreviewDialog } from "./PdfPreviewDialog";

interface Employee {
  id: string;
  name: string;
  position: string;
  employment_start_date: string | null;
}

interface CompanySettings {
  name: string | null;
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  city: string | null;
}

export const AnniversaryCertificateForm = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [previewDoc, setPreviewDoc] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  
  const [formData, setFormData] = useState({
    selectedEmployeeId: "",
    personName: "",
    yearsOfService: 0,
    documentDate: new Date().toISOString().split("T")[0],
    anniversaryDate: new Date().toISOString().split("T")[0],
    customText: "",
    position: "",
    documentTitle: "Urkunde",
    documentSubtitle: "",
  });

  const titleOptions = [
    { value: "Urkunde", label: "Urkunde" },
    { value: "Jubiläumsurkunde", label: "Jubiläumsurkunde" },
    { value: "Bescheinigung", label: "Bescheinigung" },
    { value: "Ehrenurkunde", label: "Ehrenurkunde" },
    { value: "Dankesurkunde", label: "Dankesurkunde" },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [employeesRes, companyRes] = await Promise.all([
        supabase.from("employees").select("id, name, position, employment_start_date").is("archived_at", null),
        supabase.from("company_settings").select("name, street, house_number, postal_code, city").single()
      ]);

      if (employeesRes.data) setEmployees(employeesRes.data);
      if (companyRes.data) setCompanySettings(companyRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelect = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      const years = employee.employment_start_date 
        ? differenceInYears(new Date(), new Date(employee.employment_start_date))
        : 0;

      const defaultText = getDefaultAnniversaryText(employee.name, years);

      setFormData(prev => ({
        ...prev,
        selectedEmployeeId: employeeId,
        personName: employee.name,
        position: employee.position,
        yearsOfService: years,
        customText: defaultText,
      }));
    }
  };

  const getDefaultAnniversaryText = (name: string, years: number) => {
    const yearWord = years === 1 ? "Jahr" : "Jahren";
    return `Mit großer Freude und Anerkennung würdigen wir hiermit

${name}

für ${years} ${yearWord} treue und engagierte Mitarbeit in unserem Unternehmen.

In dieser Zeit haben Sie mit Ihrer Zuverlässigkeit, Ihrem Fachwissen und Ihrem Engagement maßgeblich zum Erfolg unseres Unternehmens beigetragen.

Wir danken Ihnen herzlich für Ihre Loyalität und Ihr Vertrauen und freuen uns auf viele weitere gemeinsame Jahre der Zusammenarbeit.`;
  };

  const handleYearsChange = (years: number) => {
    const defaultText = getDefaultAnniversaryText(formData.personName, years);
    setFormData(prev => ({
      ...prev,
      yearsOfService: years,
      customText: defaultText,
    }));
  };

  const handleSave = async () => {
    if (!user) return;
    if (!formData.personName || !formData.yearsOfService) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("contract_templates").insert({
        template_type: "anniversary",
        employee_id: formData.selectedEmployeeId || null,
        person_name: formData.personName,
        position: formData.position,
        years_of_service: formData.yearsOfService,
        anniversary_date: formData.anniversaryDate,
        custom_text: formData.customText,
        status: "draft",
        created_by: user.id,
      });

      if (error) throw error;

      // Create + archive PDF to employee documents
      try {
        const pdfDoc = await exportAnniversaryCertificateToPDF(
          {
            personName: formData.personName,
            position: formData.position,
            yearsOfService: formData.yearsOfService,
            documentDate: formData.documentDate,
            anniversaryDate: formData.anniversaryDate,
            customText: formData.customText,
            companyName: companySettings?.name || "",
            companyAddress: companySettings
              ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}`
              : "",
            documentTitle: formData.documentTitle,
            documentSubtitle: formData.documentSubtitle,
          },
          false
        );

        const category = getDocumentCategory("anniversary");
        const typeName = getDocumentTypeName("anniversary");
        const result = await saveDocumentToEmployeeFile(
          formData.selectedEmployeeId,
          `${typeName} - ${formData.yearsOfService} Jahre`,
          category,
          pdfDoc,
          user.id
        );

        if (result.success) {
          toast.success("Jubiläumsurkunde gespeichert und in Dokumente abgelegt");
        } else {
          console.warn("Dokument konnte nicht gespeichert werden:", result.error);
          toast.error("Jubiläumsurkunde gespeichert, PDF-Ablage fehlgeschlagen");
        }
      } catch (e) {
        console.error("Error archiving PDF:", e);
        toast.error("Jubiläumsurkunde gespeichert, PDF-Ablage fehlgeschlagen");
      }

      resetForm();
    } catch (error) {
      console.error("Error saving anniversary:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const buildPdfPayload = () => ({
    personName: formData.personName,
    position: formData.position,
    yearsOfService: formData.yearsOfService,
    documentDate: formData.documentDate,
    anniversaryDate: formData.anniversaryDate,
    customText: formData.customText,
    companyName: companySettings?.name || "",
    companyAddress: companySettings ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}` : "",
    documentTitle: formData.documentTitle,
    documentSubtitle: formData.documentSubtitle,
  });

  const handlePreview = async () => {
    if (!formData.personName) {
      toast.error("Bitte wählen Sie einen Mitarbeiter aus");
      return;
    }
    try {
      const pdfDoc = await exportAnniversaryCertificateToPDF(buildPdfPayload(), false);
      setPreviewDoc(pdfDoc);
      setPreviewOpen(true);
    } catch (e) {
      console.error("Error generating preview:", e);
      toast.error("Fehler beim Erstellen der Vorschau");
    }
  };

  const handleExportPDF = async () => {
    if (!formData.personName || !formData.yearsOfService) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    try {
      // Pass saveToFile: false to get the PDF document without auto-downloading
      const pdfDoc = await exportAnniversaryCertificateToPDF({
        personName: formData.personName,
        position: formData.position,
        yearsOfService: formData.yearsOfService,
        documentDate: formData.documentDate,
        anniversaryDate: formData.anniversaryDate,
        customText: formData.customText,
        companyName: companySettings?.name || "",
        companyAddress: companySettings ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}` : "",
        documentTitle: formData.documentTitle,
        documentSubtitle: formData.documentSubtitle,
      }, false);

      // Save to employee documents if employee is selected
      if (formData.selectedEmployeeId && user) {
        const category = getDocumentCategory('anniversary');
        const typeName = getDocumentTypeName('anniversary');
        const result = await saveDocumentToEmployeeFile(
          formData.selectedEmployeeId,
          `${typeName} - ${formData.yearsOfService} Jahre`,
          category,
          pdfDoc,
          user.id
        );
        if (result.success) {
          toast.success("PDF wurde erstellt und in den Mitarbeiter-Dokumenten gespeichert");
        } else {
          console.warn("Dokument konnte nicht gespeichert werden:", result.error);
        }
      }

      // Always download the PDF for the user
      pdfDoc.save(`Jubilaeumsurkunde_${formData.personName.replace(/\s+/g, "_")}.pdf`);
      toast.success("PDF wurde erstellt");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Fehler beim Erstellen des PDF");
    }
  };

  const resetForm = () => {
    setFormData({
      selectedEmployeeId: "",
      personName: "",
      yearsOfService: 0,
      documentDate: new Date().toISOString().split("T")[0],
      anniversaryDate: new Date().toISOString().split("T")[0],
      customText: "",
      position: "",
      documentTitle: "Urkunde",
      documentSubtitle: "",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PartyPopper className="h-5 w-5" />
          Jubiläumsurkunde erstellen
        </CardTitle>
        <CardDescription>
          Erstellen Sie eine festliche Urkunde für Mitarbeiterjubiläen
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Employee Selection */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Mitarbeiter auswählen</h4>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Mitarbeiter *</Label>
              <Select value={formData.selectedEmployeeId} onValueChange={handleEmployeeSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Mitarbeiter wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name</Label>
              <Input 
                value={formData.personName} 
                onChange={(e) => setFormData(prev => ({ ...prev, personName: e.target.value }))}
                readOnly
                className="bg-muted"
              />
            </div>
          </div>
        </div>

        {/* Anniversary Details */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Dokumenten-Details</h4>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <Label>Dokumenten-Titel *</Label>
              <Select 
                value={formData.documentTitle} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, documentTitle: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Titel wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {titleOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="lg:col-span-2">
              <Label>Untertitel / Anlass</Label>
              <Input 
                value={formData.documentSubtitle} 
                onChange={(e) => setFormData(prev => ({ ...prev, documentSubtitle: e.target.value }))}
                placeholder="z.B. Teilnahme einer Schulung, 10-jähriges Dienstjubiläum..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Optional: Wird unter dem Titel angezeigt (z.B. für Bescheinigungen)
              </p>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label>Jahre im Unternehmen</Label>
              <Input 
                type="number"
                value={formData.yearsOfService} 
                onChange={(e) => handleYearsChange(parseInt(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label>Dokumentendatum</Label>
              <Input 
                type="date"
                value={formData.documentDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, documentDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Jubiläumsdatum</Label>
              <Input 
                type="date"
                value={formData.anniversaryDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, anniversaryDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Position</Label>
              <Input 
                value={formData.position} 
                onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Custom Text */}
        <div className="space-y-4">
          <Label>Urkundentext</Label>
          <Textarea 
            value={formData.customText} 
            onChange={(e) => setFormData(prev => ({ ...prev, customText: e.target.value }))}
            placeholder="Der Text der auf der Urkunde erscheinen soll..."
            rows={10}
            className="font-serif"
          />
          <p className="text-sm text-muted-foreground">
            Der Text wird automatisch basierend auf den Jahren generiert, kann aber angepasst werden.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={handlePreview} disabled={saving} className="gap-2">
            <Eye className="h-4 w-4" />
            Vorschau
          </Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Speichern
          </Button>
          <Button variant="outline" onClick={handleExportPDF} className="gap-2">
            <FileDown className="h-4 w-4" />
            Als Urkunde drucken (PDF)
          </Button>
        </div>
      </CardContent>
      <PdfPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        pdfDoc={previewDoc}
        title="Vorschau: Jubiläumsurkunde"
        fileName={`Jubilaeumsurkunde_${formData.personName.replace(/\s+/g, "_") || "Vorschau"}.pdf`}
      />
    </Card>
  );
};
